import { useMutation, useQueryClient } from '@tanstack/react-query';
import { publicPost } from '@/lib/api/publicClient';
import { publicConfirm } from '@/lib/api/endpoints';

export interface RescheduleRequestInput {
  preferredWindows?: string[];
  note?: string;
}

export interface CancelInput {
  reason: string;
}

/**
 * Bundles the three customer-facing actions on a confirmation token:
 * confirm, request-reschedule, cancel. Each action invalidates the
 * landing query so the UI re-reads the new `status` and CTAs collapse
 * into the success state.
 */
export function useConfirmActions(token: string) {
  const queryClient = useQueryClient();

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: ['public', 'confirm', 'landing', token],
    });
  };

  const confirm = useMutation({
    mutationFn: () => publicPost<unknown>(publicConfirm.confirm(token)),
    onSuccess: invalidate,
  });

  const reschedule = useMutation({
    mutationFn: (input: RescheduleRequestInput) =>
      publicPost<unknown, RescheduleRequestInput>(publicConfirm.reschedule(token), input),
    onSuccess: invalidate,
  });

  const cancel = useMutation({
    mutationFn: (input: CancelInput) =>
      publicPost<unknown, CancelInput>(publicConfirm.cancel(token), input),
    onSuccess: invalidate,
  });

  return { confirm, reschedule, cancel };
}
